import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withModifiers as _withModifiers, unref as _unref, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "suggestions" }
const _hoisted_2 = ["onClick"]
const _hoisted_3 = { class: "actions" }
const _hoisted_4 = {
  key: 0,
  class: "info"
}
const _hoisted_5 = { colspan: "6" }

import {ref, watch} from "vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import SogFlexColumn from "@/shared/ui/SogFlexColumn.vue";
import SogSkeleton from "@/shared/ui/SogSkeleton.vue";
import {useNavigationStore} from "@/modules/Navigation/store";
import apiVehicleRegistration from "@/modules/VehicleRegistration/api";
import notificationService from "@/shared/services/NotificationService";
import VehicleRegistrationSuggestion, {matchTypeDescription} from "@/shared/model/VehicleRegistrationSuggestion";
import {diffSegments, normalizeNumber, TextSegment} from "@/modules/VehicleRegistration/utils/suggestionDiff";

interface Props {
  vehicleRegistrationId?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VehicleRegistrationSuggestionComponent',
  props: {
    vehicleRegistrationId: { default: undefined }
  },
  emits: ["changed"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const _emit = __emit

const _navigationStore = useNavigationStore()
const _items = ref<VehicleRegistrationSuggestion[]>([])
const _loading = ref(false)

async function load() {
  _items.value = []
  if (!props.vehicleRegistrationId) {
    return
  }
  _loading.value = true
  try {
    _items.value = (await apiVehicleRegistration.suggestions(props.vehicleRegistrationId)) ?? []
  } finally {
    _loading.value = false
  }
}

watch(() => props.vehicleRegistrationId, load, {immediate: true})

/** Erfasste Nummer, die zu diesem Vorschlag geführt hat (Versicherungsschein-Nr. oder eVB-Nr.). */
function recordedNumber(item: VehicleRegistrationSuggestion): string {
  const t_candidates = item.matchType === "EvbTypo"
    ? [item.versicherungsBestaetigungsNummer]
    : [item.versicherungsScheinnummer, item.versicherungsBestaetigungsNummer]
  const t_target = normalizeNumber(item.insuranceNumber)
  let t_best = ""
  let t_bestDistance = Number.MAX_SAFE_INTEGER
  for (const t_c of t_candidates) {
    const t_norm = normalizeNumber(t_c)
    if (!t_norm) continue
    const t_d = Math.abs(t_norm.length - t_target.length) + [...t_norm].filter((c, i) => c !== t_target[i]).length
    if (t_d < t_bestDistance) {
      t_best = t_c ?? ""
      t_bestDistance = t_d
    }
  }
  return t_best
}

function recordedSegments(item: VehicleRegistrationSuggestion): TextSegment[] {
  return diffSegments(recordedNumber(item), item.insuranceNumber ?? "").recorded
}

function suggestedSegments(item: VehicleRegistrationSuggestion): TextSegment[] {
  return diffSegments(recordedNumber(item), item.insuranceNumber ?? "").suggested
}

function formatDate(value?: Date | string): string {
  if (!value) return ""
  const t_date = new Date(value)
  return isNaN(t_date.getTime()) ? "" : t_date.toLocaleDateString("de-DE")
}

/** Zeigt die vorgeschlagene Police in der Versicherungskarte (Seitenleiste) an. */
function onShowInsurance(item: VehicleRegistrationSuggestion) {
  if (item.insuranceId) {
    _navigationStore.currentInsuranceId = item.insuranceId
  }
}

function onAccept(item: VehicleRegistrationSuggestion) {
  if (!item.id) return
  notificationService.showYesNo(
    "Vorschlag übernehmen",
    "Soll die Zulassung mit der Police " + item.insuranceNumber + " verknüpft werden?",
    async () => {
      const t_res = await apiVehicleRegistration.acceptSuggestion(item.id ?? "")
      if (t_res) {
        notificationService.success("Zulassung wurde mit der Police " + item.insuranceNumber + " verknüpft")
      }
      // auch bei Fehler neu laden: der Vorschlag kann inzwischen veraltet sein
      await load()
      _emit("changed")
    })
}

async function onReject(item: VehicleRegistrationSuggestion) {
  if (!item.id) return
  const t_res = await apiVehicleRegistration.rejectSuggestion(item.id, undefined)
  if (t_res) {
    notificationService.success("Vorschlag wurde abgelehnt")
    await load()
    _emit("changed")
  }
}

async function onRejectAll() {
  if (!props.vehicleRegistrationId) return
  const t_res = await apiVehicleRegistration.rejectSuggestion(undefined, props.vehicleRegistrationId)
  if (t_res) {
    notificationService.success("Alle Vorschläge wurden abgelehnt")
    await load()
    _emit("changed")
  }
}

__expose({load})

return (_ctx: any,_cache: any) => {
  return _withDirectives((_openBlock(), _createBlock(SogCard, null, {
    default: _withCtx(() => [
      _createVNode(SogCardTitle, null, {
        default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
          _createTextVNode("Vorschläge für die Verknüpfung", -1)
        ]))]),
        _: 1
      }),
      _withDirectives(_createVNode(SogCardContent, null, {
        default: _withCtx(() => [
          _createVNode(SogFlexColumn, null, {
            default: _withCtx(() => [
              _cache[2] || (_cache[2] = _createElementVNode("div", { class: "hint" }, " Die erfasste Nummer passt zu keiner Police. Folgende Policen könnten gemeint sein – bitte prüfen und übernehmen. ", -1)),
              _createElementVNode("table", _hoisted_1, [
                _cache[1] || (_cache[1] = _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", null, "Erfasst"),
                    _createElementVNode("th", null, "Vorschlag Police"),
                    _createElementVNode("th", null, "Abweichung"),
                    _createElementVNode("th", null, "Sicherheit"),
                    _createElementVNode("th", null, "Police erstellt"),
                    _createElementVNode("th")
                  ])
                ], -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_items.value, (item) => {
                  return (_openBlock(), _createElementBlock("tbody", {
                    key: item.id
                  }, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("td", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recordedSegments(item), (seg, i) => {
                          return (_openBlock(), _createElementBlock("span", {
                            key: i,
                            class: _normalizeClass({diff: seg.diff})
                          }, _toDisplayString(seg.text), 3))
                        }), 128))
                      ]),
                      _createElementVNode("td", null, [
                        _createElementVNode("a", {
                          class: "policy-link",
                          href: "#",
                          title: "Police in der Vorschau anzeigen",
                          onClick: _withModifiers(($event: any) => (onShowInsurance(item)), ["prevent"])
                        }, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestedSegments(item), (seg, i) => {
                            return (_openBlock(), _createElementBlock("span", {
                              key: i,
                              class: _normalizeClass({diff: seg.diff})
                            }, _toDisplayString(seg.text), 3))
                          }), 128))
                        ], 8, _hoisted_2)
                      ]),
                      _createElementVNode("td", null, _toDisplayString(_unref(matchTypeDescription)(item.matchType)), 1),
                      _createElementVNode("td", null, [
                        _createElementVNode("span", {
                          class: _normalizeClass(['confidence', 'confidence-' + item.confidence])
                        }, _toDisplayString(item.confidenceText), 3)
                      ]),
                      _createElementVNode("td", null, _toDisplayString(formatDate(item.insuranceCreateDate)), 1),
                      _createElementVNode("td", _hoisted_3, [
                        _createVNode(SogCardActionButton, {
                          "mdi-icon": "mdi-check",
                          tooltip: "Vorschlag übernehmen",
                          onClick: ($event: any) => (onAccept(item))
                        }, null, 8, ["onClick"]),
                        _createVNode(SogCardActionButton, {
                          "mdi-icon": "mdi-close",
                          tooltip: "Vorschlag ablehnen",
                          onClick: ($event: any) => (onReject(item))
                        }, null, 8, ["onClick"])
                      ])
                    ]),
                    (item.neighborhoodText)
                      ? (_openBlock(), _createElementBlock("tr", _hoisted_4, [
                          _createElementVNode("td", _hoisted_5, _toDisplayString(item.neighborhoodText), 1)
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                }), 128))
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 512), [
        [_vShow, !_loading.value]
      ]),
      _createVNode(SogCardContent, null, {
        default: _withCtx(() => [
          _withDirectives(_createVNode(SogSkeleton, null, null, 512), [
            [_vShow, _loading.value]
          ])
        ]),
        _: 1
      }),
      _withDirectives(_createVNode(SogCardActions, null, {
        default: _withCtx(() => [
          _createVNode(SogCardActionButton, {
            "mdi-icon": "mdi-close-circle-multiple",
            tooltip: "Alle Vorschläge ablehnen",
            onClick: onRejectAll
          })
        ]),
        _: 1
      }, 512), [
        [_vShow, !_loading.value && _items.value.length > 1]
      ])
    ]),
    _: 1
  }, 512)), [
    [_vShow, _loading.value || _items.value.length > 0]
  ])
}
}

})